import { useEffect, useRef, useState } from 'react'
import { ExternalLink, Linkedin } from 'lucide-react'

interface InsightArticle {
  id: string
  title: string
  category: string
  date: string
  linkedInUrl: string
}

const articles: InsightArticle[] = [
  {
    id: 'human-centered',
    title: 'The Human-Centered Approach to EdTech Strategy',
    category: 'Strategic EdTech',
    date: 'July 2024',
    linkedInUrl: 'https://www.linkedin.com/in/giovanniduarte',
  },
  {
    id: 'balance-strategy',
    title: 'Achieving Balance in Your EdTech Strategy',
    category: 'Digital Transformation',
    date: 'August 2024',
    linkedInUrl: 'https://www.linkedin.com/in/giovanniduarte',
  },
  {
    id: 'ai-contextual',
    title: 'The Power of AI in Education: Contextual Data',
    category: 'AI & Data',
    date: 'August 2024',
    linkedInUrl: 'https://www.linkedin.com/in/giovanniduarte',
  },
]

export const Insights: React.FC = () => {
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set())
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id')
            if (id) {
              setVisibleCards((prev) => new Set([...prev, id]))
            }
          }
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px',
      }
    )

    const cards = sectionRef.current?.querySelectorAll('[data-id]')
    cards?.forEach((card) => observer.observe(card))

    return () => observer.disconnect()
  }, [])

  return (
    <section
      id="insights"
      ref={sectionRef}
      className="section-alt scroll-mt-20"
      aria-labelledby="insights-heading"
    >
      <div className="container-narrow">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            id="insights-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            Insights
          </h2>
          <p className="text-lg text-slate-600">
            Thoughts on EdTech strategy, learning innovation, and the future of education.
          </p>
        </div>

        {/* Articles List */}
        <div
          className="space-y-4"
          role="list"
          aria-label="Featured articles"
        >
          {articles.map((article, index) => (
            <a
              key={article.id}
              data-id={article.id}
              href={article.linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`block bg-white rounded-xl p-6 border border-slate-200 hover:border-brand-blue/30 hover:shadow-lg transition-all duration-500 group ${
                visibleCards.has(article.id)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: `${index * 100}ms`,
              }}
              role="listitem"
              aria-label={`Read "${article.title}" on LinkedIn`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-blue bg-brand-blue/10 px-2 py-1 rounded">
                      {article.category}
                    </span>
                    <span className="text-sm text-slate-400">{article.date}</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-900 group-hover:text-brand-blue transition-colors">
                    {article.title}
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-slate-400 group-hover:text-brand-blue transition-colors">
                  <Linkedin size={20} aria-hidden="true" />
                  <ExternalLink size={16} aria-hidden="true" />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* LinkedIn CTA */}
        <div className="text-center mt-10">
          <a
            href="https://www.linkedin.com/in/giovanniduarte"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-brand-blue hover:text-brand-blue-dark font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 rounded"
          >
            <Linkedin size={20} aria-hidden="true" />
            Follow on LinkedIn for more insights
            <ExternalLink size={14} aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  )
}
